import './SettingsPage.css'
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import RootState from "../../redux/states/root.state";
import { logout } from "../../redux/actions/user.actions";
import { AppDispatch } from "../../redux/store";
import Header from "../../components/common/Header/Header";
import usePermissionCheck from '../../hooks/usePermissionCheck';
import { getPreferredUnit, setPreferredUnit } from "../../utils/temperature";
import { localizeSpa } from "../../utils/brand";
import {
  ChevronRightIcon,
  DropletIcon,
  LockLineIcon,
  LogoutLineIcon,
  MoonIcon,
  PersonIcon,
  SunIcon,
  ThermometerIcon,
  UserCogIcon,
} from "../../components/common/Icons/Icons";

const Settings = () => {
  const dispatch: AppDispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state: RootState) => state.user.data);
  const {canAccess:canImpersonate} = usePermissionCheck(['can_impersonate_user']);
  const [theme, setTheme] = useState<any>('light');
  const [impersonate, setImpersonate] = useState<any>(null);
  const [tempUnit, setTempUnit] = useState<'F' | 'C'>(getPreferredUnit());
  var mode = localStorage.getItem('data-theme');
  var impersonating = sessionStorage.getItem("impersonate");

  useEffect(() => {
    if (mode) {
      setTheme(mode);
    } else if (window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark');
    }
  }, [mode]);

  useEffect(() => {
    if (impersonating) {
      setImpersonate(impersonating);
    } else {
      setImpersonate(null);
    }
  }, [impersonating]);

  useEffect(() => {
    // Update temperature unit when localStorage changes
    const handleStorageChange = () => {
      setTempUnit(getPreferredUnit());
    };
    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        navigate("/");
      })
      .catch((error) => {
        console.error("Logout failed:", error);
      });
  };

  const handleChange = () => {
    if (theme==='dark'){
      setTheme('light');
      document.body.setAttribute('data-theme', 'light');
      localStorage.setItem('data-theme', 'light');
    } else {
      setTheme('dark');
      document.body.setAttribute('data-theme', 'dark');
      localStorage.setItem('data-theme', 'dark');
    }
    window.dispatchEvent( new Event('storage') )
  }

  // Same localStorage preference the old Units page saved; the storage event lets other views pick it up
  const handleUnitChange = (unit: 'F' | 'C') => {
    if (unit === tempUnit) return;
    setPreferredUnit(unit);
    setTempUnit(unit);
    window.dispatchEvent( new Event('storage') )
  }

  const handleImpersonate = () => {
    if (impersonate!==null){
      setImpersonate(null);
      sessionStorage.removeItem("impersonate");
    } else {
      setImpersonate('customer');
      sessionStorage.setItem("impersonate", "customer");
    }
    window.dispatchEvent( new Event('impersonate') )
  }

  const impersonationOn = impersonate!==null && canImpersonate;

  return (
      <div className="page st-page">
        <Header withLogo={false} withNavBtn={false} withUserAvatar={false}/>
        <main className="st-main">
            <div className="st-heading">
                <h1 className="page-heading">Settings</h1>
            </div>

            <section className="st-group" aria-labelledby="st-account">
                <h2 className="st-group-title" id="st-account">Account</h2>
                <ul className="card st-list">
                    <li>
                        <Link to="/settings/edit-profile" className="st-row">
                            <span className="st-row-icon"><PersonIcon/></span>
                            <span className="st-row-label">Edit profile</span>
                            <span className="st-row-end"><ChevronRightIcon/></span>
                        </Link>
                    </li>
                    <li>
                        <Link to="/settings/change-password" className="st-row">
                            <span className="st-row-icon"><LockLineIcon/></span>
                            <span className="st-row-label">Change password</span>
                            <span className="st-row-end"><ChevronRightIcon/></span>
                        </Link>
                    </li>
                    <li>
                        <Link to="/my-spas" state={{ isSettings: true }} className="st-row">
                            <span className="st-row-icon"><DropletIcon/></span>
                            <span className="st-row-label">{localizeSpa("My Spas")}</span>
                            <span className="st-row-end"><ChevronRightIcon/></span>
                        </Link>
                    </li>
                    {canImpersonate &&
                    <li>
                        <div className="st-row">
                            <span className="st-row-icon"><UserCogIcon/></span>
                            <span className="st-row-label" id="st-impersonate-label">
                                Impersonation
                                <span className="st-row-hint">See the portal as a customer would</span>
                            </span>
                            <button
                              type="button"
                              role="switch"
                              className="st-switch"
                              aria-checked={impersonationOn}
                              aria-labelledby="st-impersonate-label"
                              onClick={handleImpersonate}
                            />
                        </div>
                    </li>}
                </ul>
            </section>

            <section className="st-group" aria-labelledby="st-general">
                <h2 className="st-group-title" id="st-general">General</h2>
                <ul className="card st-list">
                    <li>
                        <div className="st-row">
                            <span className="st-row-icon"><ThermometerIcon/></span>
                            <span className="st-row-label" id="st-unit-label">Temperature</span>
                            <div className="segmented" role="radiogroup" aria-labelledby="st-unit-label">
                                {(['F', 'C'] as const).map((unit) => (
                                    <button key={unit} type="button" role="radio" aria-checked={tempUnit===unit} className={tempUnit===unit ? 'active' : ''} onClick={() => handleUnitChange(unit)}>
                                        °{unit}
                                    </button>
                                ))}
                            </div>
                        </div>
                    </li>
                    <li>
                        <div className="st-row">
                            <span className="st-row-icon">{theme==='dark' ? <MoonIcon/> : <SunIcon/>}</span>
                            <span className="st-row-label" id="st-theme-label">Appearance</span>
                            <div className="segmented" role="radiogroup" aria-labelledby="st-theme-label">
                                <button type="button" role="radio" aria-checked={theme!=='dark'} className={theme!=='dark' ? 'active' : ''} onClick={() => theme==='dark' && handleChange()}>
                                    <SunIcon/><span className="st-seg-label">Light</span>
                                </button>
                                <button type="button" role="radio" aria-checked={theme==='dark'} className={theme==='dark' ? 'active' : ''} onClick={() => theme!=='dark' && handleChange()}>
                                    <MoonIcon/><span className="st-seg-label">Dark</span>
                                </button>
                            </div>
                        </div>
                    </li>
                </ul>
            </section>

            <ul className="card st-list">
                <li>
                    {user ?
                      <button type="button" className="st-row danger" onClick={handleLogout}>
                          <span className="st-row-icon"><LogoutLineIcon/></span>
                          <span className="st-row-label">Log out</span>
                      </button>
                      :
                      <Link to="/login" className="st-row">
                          <span className="st-row-icon"><LogoutLineIcon/></span>
                          <span className="st-row-label">Log in</span>
                          <span className="st-row-end"><ChevronRightIcon/></span>
                      </Link>}
                </li>
            </ul>
        </main>
      </div>
  );
};

export default Settings;
